/**
 * Dot-notation nested key utilities for manifest field keys.
 *
 * Manifest fields can use dotted keys like "meta.tune" or "meta.settings.color"
 * which map to nested objects: { meta: { tune: value } }.
 *
 * React-hook-form already handles dot-notation natively in Controller name prop,
 * form.watch(), and form.setValue(). These utilities help the rest of the system
 * (defaults, display options, validation) work with nested structures.
 */

/** Check if a key contains dots (is a nested path) */
export function isNestedKey(key: string): boolean {
  return key.includes('.');
}

/** Get value at a dot-notation path. Returns undefined if any segment is missing. */
export function getNested(obj: Record<string, unknown>, path: string): unknown {
  if (!isNestedKey(path)) return obj[path];

  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Set value at a dot-notation path, creating intermediate objects as needed. */
export function setNested(obj: Record<string, unknown>, path: string, value: unknown): void {
  if (!isNestedKey(path)) {
    obj[path] = value;
    return;
  }

  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!(part in current) || typeof current[part] !== 'object' || current[part] === null) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

/**
 * Walk a nested object and return every dotted leaf path that holds a truthy value.
 *
 * Used to translate RHF's `formState.dirtyFields` (shape `{ a: { b: true } }`) into
 * a flat list of dotted paths (`['a.b']`) so we can compare them against the
 * full-path entries in `hiddenCompoundFormKeys`. Without flattening, a top-level
 * dotted key like `options` (containing one dirty leaf) collides with a hidden
 * compound's `options.*` namespace and the legitimate dirty change is wrongly
 * stripped from the Save-button dirty check.
 *
 * Arrays are treated as leaves (RHF marks the whole array dirty, not per index).
 * Empty objects are skipped — they cannot represent a dirty leaf.
 */
export function flattenDottedKeys(obj: unknown, prefix = ''): string[] {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj ? (prefix ? [prefix] : []) : [];
  }
  const out: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flattenDottedKeys(v, path));
    } else if (v) {
      out.push(path);
    }
  }
  return out;
}
